<template>
  <Teleport to="body">
    <div
      v-if="dialog.state.open"
      class="dialog-mask"
      role="presentation"
      @click.self="onMask"
    >
      <div
        class="dialog-panel"
        role="alertdialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="descId"
      >
        <h2 :id="titleId" class="dialog-title">{{ dialog.state.title }}</h2>
        <p :id="descId" class="dialog-message">{{ dialog.state.message }}</p>
        <div class="dialog-actions">
          <button
            v-if="dialog.state.mode === 'confirm'"
            ref="cancelRef"
            type="button"
            class="btn"
            @click="dialog.dismiss()"
          >
            {{ dialog.state.cancelText }}
          </button>
          <button
            ref="confirmRef"
            type="button"
            class="btn"
            :class="dialog.state.tone === 'danger' ? 'btn-danger solid' : 'btn-primary'"
            @click="dialog.accept()"
          >
            {{ dialog.state.confirmText }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useDialogStore } from '@/stores/dialog'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const dialog = useDialogStore()
const titleId = 'app-dialog-title'
const descId = 'app-dialog-desc'
const confirmRef = ref<HTMLButtonElement | null>(null)
const cancelRef = ref<HTMLButtonElement | null>(null)

useBodyScrollLock(computed(() => dialog.state.open))

function onMask() {
  // Click outside = cancel for confirm; dismiss alert
  dialog.dismiss()
}

function onKey(e: KeyboardEvent) {
  if (!dialog.state.open) return
  if (e.key === 'Escape') {
    e.preventDefault()
    dialog.dismiss()
  } else if (e.key === 'Enter') {
    e.preventDefault()
    dialog.accept()
  }
}

watch(
  () => dialog.state.open,
  async (open) => {
    if (open) {
      document.addEventListener('keydown', onKey)
      await nextTick()
      // Focus primary action for keyboard flow
      if (dialog.state.mode === 'confirm') {
        cancelRef.value?.focus()
      } else {
        confirmRef.value?.focus()
      }
    } else {
      document.removeEventListener('keydown', onKey)
    }
  },
)
</script>

<style scoped>
.dialog-mask {
  position: fixed;
  inset: 0;
  z-index: calc(var(--z-modal) + 10);
  display: grid;
  place-items: center;
  padding: 20px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  overflow: hidden;
  overscroll-behavior: none;
}

.dialog-panel {
  width: min(400px, 100%);
  max-width: 100%;
  min-width: 0;
  max-height: min(88dvh, 88vh);
  box-sizing: border-box;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  padding: 22px 22px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;
}

.dialog-title {
  margin: 0;
  flex-shrink: 0;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--color-foreground);
  word-break: break-word;
}

.dialog-message {
  margin: 0;
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  font-size: var(--font-size-base);
  line-height: 1.55;
  color: var(--color-on-surface-muted);
  white-space: pre-wrap;
  word-break: break-word;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  flex-shrink: 0;
  gap: 8px;
  margin-top: 4px;
}

.dialog-actions .btn {
  min-width: 72px;
  min-height: 36px;
  height: 36px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  text-align: center;
  box-sizing: border-box;
}

@media (max-width: 720px) {
  .dialog-mask {
    padding: max(12px, env(safe-area-inset-top, 0px))
      max(12px, env(safe-area-inset-right, 0px))
      max(12px, env(safe-area-inset-bottom, 0px))
      max(12px, env(safe-area-inset-left, 0px));
    place-items: center;
  }

  .dialog-panel {
    width: 100%;
    max-width: 100%;
    padding: 18px 16px 16px;
  }

  .dialog-actions {
    width: 100%;
  }

  .dialog-actions:has(> .btn:nth-child(2):last-child) > .btn,
  .dialog-actions:has(> .btn:only-child) > .btn {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 44px;
    height: 44px;
  }
}

.dialog-actions .btn-danger.solid {
  background: var(--color-destructive);
  color: #fff;
  border-color: var(--color-destructive);
}

.dialog-actions .btn-danger.solid:hover:not(:disabled) {
  filter: brightness(0.95);
  background: var(--color-destructive);
}
</style>
